import { createSelector } from "reselect";
import get from "lodash/get";
import {
	calculateSDPBasePrice,
	getOngoingSDPProducts,
	getSDPFlights,
} from "app/pages/SmartDP/smartDPSelectors";
import {
	DESTINATION_LEVELS,
	PRICING_SUPPLEMENT,
	QUOTATION_ITEMS_TYPE,
	RATING_TYPE,
	TRIP_ADVISOR_RATING_MIN,
} from "app/constants";
import {
	getFlightUpgrade,
	getLuggageTotalPrice,
	getTravellersTotalCount,
} from "app/pages/Booking/bookingSelectors";
import { buildSDPBudgetFilterValues, getAccommodationPrice } from "app/utils/smartDPUtils";
import some from "lodash/some";
import {
	getBoardsDiscountPercentage,
	getBoardsDiscountValue,
	getDiscountBadgeVisibility,
} from "app/utils/discountUtils";

const getQuotationItems = state => state?.smartDP?.quotationItems || [];
const getHideDiscountHBX = state => state?.brand?.hideDiscountHBX;
const getDisplayMiles = state => state.brand?.displayMiles;

export const getSDPProductsRating = state =>
	state?.partner?.marketing?.productRating || RATING_TYPE.TRIP_ADVISOR;

/**
 * Convertit une note en un tableau de notes pour le filtrage
 * doit se lire comme suit : est-ce que rating doit apparaitre dans le filtre si le user sélectionne une note dans l'array
 * 4 gives [4]
 * 4.4 gives [4]
 * 4.5 gives [4.5, 4]
 * 4.6 gives [4.5, 4]
 * 5 gives [5, 4.5, 4]
 * @param {number} rating - La note initiale (entre 4 et 5)
 * @return {number[]} Tableau de notes pour le filtrage
 */

export const convertRatingToArray = rating => {
	// Arrondir au 0.5 inférieur
	const roundedRating = Math.floor(rating * 2) / 2;

	// Initialiser le tableau de résultat
	const result = [];

	// Ajouter la note arrondie et toutes les notes inférieures par palier de 0.5
	for (let r = roundedRating; r >= TRIP_ADVISOR_RATING_MIN; r -= 0.5) {
		result.push(r);
	}

	return result;
};

export const getOngoingSDPProductsNormalize = createSelector(
	[
		getSDPProductsRating,
		getOngoingSDPProducts,
		calculateSDPBasePrice,
		getTravellersTotalCount,
		getFlightUpgrade,
		getLuggageTotalPrice,
	],
	(
		productRating,
		products = [],
		basePrice = 0,
		travellersTotalCount,
		flightUpgradePrice = {},
		luggageUpgradePrice = 0
	) => {
		const budgetFilterValues = buildSDPBudgetFilterValues(
			products,
			basePrice,
			travellersTotalCount
		);

		const normalizedProducts = products.map(product => {
			if (productRating === RATING_TYPE.TRIP_ADVISOR) {
				/**
				 * astuce pour pouvoir filtrer par note trip advisor 3.5+, 4+, 4,5+ et 5
				 * Il fallait donc à partir de la note trip advisor constituer un tableau de notes pour que immfacet puisse l'exploiter
				 */
				const tripadvisor = get(product, "tripadvisor", {});

				if (tripadvisor.rating >= TRIP_ADVISOR_RATING_MIN) {
					const ratingFilterValues = convertRatingToArray(tripadvisor.rating);

					product.tripadvisor = {
						...tripadvisor,
						ratingFilterValues,
					};
				}
			}

			if (productRating === RATING_TYPE.HOLIDAY_CHECK) {
				const holidaycheck = get(product, "holidaycheck", {});
				let ratingFilterValues = [];
				if (holidaycheck.recommendation >= 90) {
					ratingFilterValues = [70, 80, 90];
				} else if (holidaycheck.recommendation >= 80) {
					ratingFilterValues = [70, 80];
				} else {
					ratingFilterValues = [70];
				}
				product.holidaycheck = {
					...holidaycheck,
					ratingFilterValues,
				};
			}

			product.total = getAccommodationPrice(
				product,
				basePrice,
				travellersTotalCount,
				flightUpgradePrice?.upgradePrice,
				luggageUpgradePrice
			);
			product.budgetMinValue = get(
				budgetFilterValues.find(
					budget =>
						(product.total >= budget.min && product.total < budget.max) ||
						(product.total >= budget.min && !budget.max)
				),
				"min"
			);

			const levels = product.geography?.levels;
			if (
				levels?.length > 0 &&
				!some(levels, level => {
					return level.level === DESTINATION_LEVELS.DISTRICT;
				})
			) {
				levels.push({ level: DESTINATION_LEVELS.OTHER, label: DESTINATION_LEVELS.OTHER });
			}

			return product;
		});
		return normalizedProducts;
	}
);

const calculatePerTravellerPrice = (price = 0, travellersTotalCount = 0) =>
	price > 0 && travellersTotalCount > 0 ? Math.ceil(price / travellersTotalCount) : 0;

const buildSupplements = ({ flight, luggage }) =>
	[
		flight > 0 && { label: PRICING_SUPPLEMENT.FLIGHT_SUPPLEMENT, amount: flight },
		luggage > 0 && { label: PRICING_SUPPLEMENT.BAGGAGE_SUPPLEMENT, amount: luggage },
	].filter(Boolean);

const findFlightByCode = flights => code => flights.find(flight => flight?.code === code);

const EMPTY_ACCOMMODATION_AND_BOARD = {
	accommodationLabel: undefined,
	boardCode: undefined,
	boardLabel: undefined,
	accommodationUpgradePrice: 0,
	boardUpgradePrice: 0,
};

const getCheapestAccommodationAndBoard = accommodation => {
	const cheapestItem = accommodation?.accommodationItems?.[0];

	if (!cheapestItem) {
		return EMPTY_ACCOMMODATION_AND_BOARD;
	}

	const boards = cheapestItem.boards ?? cheapestItem.codes ?? [];
	const selectedBoard = boards[0] ?? {};
	const boardCode = selectedBoard?.boardCode;

	const itemCode = cheapestItem?.items?.[0]?.accommodationItemCode;

	const accommodationLabel = accommodation?.accommodationItemDescriptions?.find(
		description => description?.accommodationItemCode === itemCode
	)?.label;

	const boardLabel = accommodation?.boards?.find(board => board?.boardCode === boardCode)?.label;

	return {
		accommodationLabel,
		boardCode,
		boardLabel,
		accommodationUpgradePrice: cheapestItem.upgradePrice ?? 0,
		boardUpgradePrice: selectedBoard?.upgradePrice ?? 0,
	};
};

const enrichAccommodation = ({
	accommodation,
	baseTripPerTraveller,
	flightSupplement,
	luggageSupplement,
	tripLabel,
	findFlight,
	perTraveller,
}) => {
	const cheapest = getCheapestAccommodationAndBoard(accommodation);
	const flightDetails = findFlight(accommodation?.flightCode);

	const accommodationUpgrade = perTraveller(cheapest.accommodationUpgradePrice);
	const boardUpgrade = perTraveller(cheapest.boardUpgradePrice);

	const basePricePerTraveller = baseTripPerTraveller + accommodationUpgrade + boardUpgrade;
	const total = basePricePerTraveller + flightSupplement + luggageSupplement;

	return {
		...accommodation,
		accommodationLabel: cheapest.accommodationLabel,
		boardCode: cheapest.boardCode,
		boardLabel: cheapest.boardLabel,
		total,
		basePrice: { label: tripLabel, amount: basePricePerTraveller },
		upgradePrice: buildSupplements({ flight: flightSupplement, luggage: luggageSupplement }),
		...(flightDetails ? { flightDetails } : {}),
	};
};

export const getSDPProducts = createSelector(
	[
		getOngoingSDPProductsNormalize,
		calculateSDPBasePrice,
		getTravellersTotalCount,
		getSDPFlights,
		getFlightUpgrade,
		getLuggageTotalPrice,
		getQuotationItems,
		getHideDiscountHBX,
		getDisplayMiles,
	],
	(
		accommodations = [],
		baseTripPrice = 0,
		travellersCount = 0,
		flights = [],
		flightUpgrade = {},
		luggageUpgradePrice = 0,
		quotationItems = [],
		hideDiscountHBX,
		displayMiles
	) => {
		const perTraveller = price => calculatePerTravellerPrice(price, travellersCount);

		const tripLabel =
			quotationItems.find(item => item?.type === QUOTATION_ITEMS_TYPE.TRIP)?.description ||
			"";

		const baseTripPerTraveller = perTraveller(baseTripPrice);
		const flightSupplement = perTraveller(flightUpgrade?.upgradePrice);
		const luggageSupplement = perTraveller(luggageUpgradePrice);

		const findFlight = findFlightByCode(flights);

		return accommodations
			.map(accommodation =>
				enrichAccommodation({
					accommodation,
					baseTripPerTraveller,
					flightSupplement,
					luggageSupplement,
					tripLabel,
					findFlight,
					perTraveller,
				})
			)
			?.map(accommodation => {
				return {
					...accommodation,
					discountValue: getBoardsDiscountValue(accommodation),
					discountPercentage: getBoardsDiscountPercentage(accommodation),
					discountBadgeVisibility: getDiscountBadgeVisibility(accommodation),
					hideDiscountHBX,
					displayMiles,
				};
			});
	}
);

export const getBoardTypesDescriptions = state =>
	state.smartDP.boardTypeDescriptions.map(boardType => ({
		...boardType,
		boardTypeCode: boardType.code,
	}));
